import {
  type AnyServiceConfig,
  getServiceInfo,
  getServiceObjectFields,
  type ProjectSchemaJSON,
  SERVICE_OBJECT_PATTERN_NAME,
  type ServiceConfigJSON,
  type ServiceConfigUpdate,
  type ShapeWithObjectSchema
} from '@takeshape/schema';
import { getServiceProvider } from '@takeshape/service-providers';
import {
  clearScratchState,
  fetchSchemaRoutine,
  type PostSchemaRoutineMeta,
  postSchemaRoutine,
  setUseScratchForPreview,
  updateSchema
} from 'app/branches/actions';
import { DEFAULT_BRANCH, SCRATCH_BRANCH } from 'app/branches/constants';
import { waitForFetchSchema, waitForPostSchema } from 'app/branches/sagas/fetch-schema';
import { getBranchQueryArgs, getScratchUserSchema, getUserSchema } from 'app/branches/selectors';
import type { BranchQueryArgs } from 'app/branches/types';
import { getToken } from 'app/common/utils/get-token';
import { getSavingErrorMessage } from 'app/common/utils/handle-error';
import { getStorageChannel } from 'app/common/utils/local-storage-saga';
import { getRoutineSaga } from 'app/common/utils/new-sagas';
import type { ProjectSchemaCompat } from 'app/compat/types';
import { apiEndpoint } from 'app/config';
import { showMessage } from 'app/messages/actions';
import { showErrorMessageSaga, showMessageSaga } from 'app/messages/saga';
import { getCurrentProjectId, getDefaultTimezone } from 'app/project/selectors';
import { getSchema, getServiceById, getServiceConfigDialogId } from 'app/schema/selectors';
import { closeServiceConfigDialog } from 'app/schema/slice';
import { submitFormCancelled } from 'app/schema-form/actions';
import { getForm } from 'app/schema-form/selectors';
import { OAUTH_ERROR_KEY } from 'app/sso/saga';
import pick from 'lodash/pick';
import qs from 'query-string';
import type { SagaIterator } from 'redux-saga';
import { all, type CallEffect, call, put, race, select, take, takeLatest } from 'redux-saga/effects';
import shortid from 'shortid';
import type { ActionType } from 'typesafe-actions';
import { useWorkingCopyStore } from '../branches/store/working-copy-store.js';
import {
  authFlow,
  bootstrapCancel,
  bootstrapConfirm,
  clearServiceHealthChecks,
  hideInvalidConfiguration,
  type RunBootstrapRoutineMeta,
  type RunBootstrapRoutineRequested,
  runBootstrapRoutine,
  type SaveServiceRoutinePayload,
  saveServiceRoutine,
  setBootstrapDialogProvider,
  setServiceHealthCheck,
  setShouldClearServiceHealthChecks,
  showInvalidConfiguration,
  startBootstrap,
  toggleBootstrapDialog
} from './actions';
import {
  bootstrapShopify,
  type CheckServiceHealthData,
  checkServiceHealthMutation,
  encryptServiceAuth
} from './api.js';
import { SERVICE_FORM_NAME } from './constants.js';
import providers from './providers/index.js';
import { getServiceHealthChecks } from './selectors.js';
import type { ServiceHealth } from './types.js';
import { getServicePatternSchemaUpdate } from './util/get-service-schema.js';
import { formatServiceHealth } from './util/service-health.js';

export const WORKING_COPY_ORIGIN_SERVICE_PANEL = 'WORKING_COPY_ORIGIN_SERVICE_PANEL';
/**
 * Options that are not overwritten when updating a service
 */
const PRESERVED_OPTIONS = ['spec', 'transformVersion', 'introspectedAt'];

export function shouldUpdateAuthString(config: ServiceConfigUpdate, dirty: string[]) {
  const auth = config.authentication;
  if (!auth) {
    return false;
  }

  for (const dirtyField of dirty) {
    if (dirtyField === 'authentication' || dirtyField.startsWith('authentication.')) {
      return true;
    }
  }

  return false;
}

export function* checkServiceHealth(serviceId: string, serviceConfig: ServiceConfigJSON): SagaIterator {
  const timezone: string = yield select(getDefaultTimezone);
  const branchArgs: BranchQueryArgs = yield select(getBranchQueryArgs, {});

  const result: CheckServiceHealthData = yield call(checkServiceHealthMutation, {
    input: { ...branchArgs, ...serviceConfig, id: serviceId }
  });

  const format = formatServiceHealth({ timezone });

  // Not all service types have health checks a check still needs to be set to ensure an inaccurate
  // health isn't reported
  const healthCheck = format(result.serviceHealth ?? { serviceId, status: 'empty' });

  if (healthCheck.status === 'down') {
    yield put(showInvalidConfiguration(healthCheck));

    const { payload: keepEditing } = yield take(hideInvalidConfiguration);

    if (!keepEditing) {
      yield put(setServiceHealthCheck(healthCheck));
    }

    return !keepEditing;
  }

  yield put(setServiceHealthCheck(healthCheck));

  return true;
}

export function* getAuthorizeUrl(providerName: string, serviceId?: string): SagaIterator {
  const projectId: string = yield select(getCurrentProjectId);
  const serviceProvider = providers[providerName] || getServiceProvider(providerName);
  const form = yield select(getForm, { formName: SERVICE_FORM_NAME });
  const authToken = yield select(getToken);
  const path = `${apiEndpoint}/project/${projectId}/oauth/${serviceProvider.name}/authorize`;
  const formData = form.get('data') ? form.get('data').toJS() : {};
  return `${path}?${qs.stringify({ ...formData, auth: authToken, serviceId })}`;
}

export function* oauthFlow(flowAction: ReturnType<typeof authFlow.requested>): SagaIterator {
  const { providerName } = flowAction.payload;

  const serviceProvider = providers[providerName] || getServiceProvider(providerName);

  const { title, name } = serviceProvider;

  let { serviceId } = flowAction.payload;

  if (!serviceId) {
    const form = yield select(getForm, { formName: SERVICE_FORM_NAME });
    const formData = form.get('data') ? form.get('data').toJS() : {};
    serviceId = serviceProvider.createId(formData);
  }

  const url = yield call(getAuthorizeUrl, providerName, serviceId);
  const key = `oauth-response-${name}`;

  // Clean up before starting, if the tab was previously closed during the process you could already have
  // the same key/value in localstorage which would prevent the storage event from firing.
  yield call([window.localStorage, 'removeItem'], key);

  yield call([window, 'open'], url);

  const storageChannel = yield call(getStorageChannel, key);
  const errorChannel = yield call(getStorageChannel, OAUTH_ERROR_KEY);

  const { storageEvent, errorEvent } = yield race({
    storageEvent: take(storageChannel),
    errorEvent: take(errorChannel),
    cancelEvent: take(authFlow.cancelled)
  });

  if (errorEvent) {
    const errorPayload = localStorage.getItem(OAUTH_ERROR_KEY);
    yield put(authFlow.rejected(decodeURI(errorPayload ?? `A problem occurred saving ${title} credentials`)));
  }

  if (storageEvent) {
    yield put(
      fetchSchemaRoutine.requested(undefined, {
        branch: SCRATCH_BRANCH,
        runtime: true
      })
    );
    yield put(
      fetchSchemaRoutine.requested(undefined, {
        branch: SCRATCH_BRANCH,
        runtime: false
      })
    );
    yield call(waitForFetchSchema, { branch: SCRATCH_BRANCH, runtime: false });
    const newScratchSchema = yield select(getScratchUserSchema);
    const workingCopySchema = useWorkingCopyStore.getState().workingCopyCompat;
    const currentUserSchema = yield select(getUserSchema);
    const activeSchema = workingCopySchema ?? currentUserSchema;

    const shouldSave = serviceProvider.getHealthCheck
      ? yield call(checkServiceHealth, serviceId, newScratchSchema.services[serviceId])
      : true;

    if (!shouldSave) {
      yield put(authFlow.cancelled());
      yield put(submitFormCancelled(SERVICE_FORM_NAME));
      storageChannel.close();
      errorChannel.close();
      return;
    }

    yield call(
      useWorkingCopyStore.getState().setWorkingCopy,
      {
        ...activeSchema,
        services: {
          ...activeSchema.services,
          [serviceId]: newScratchSchema.services[serviceId]
        }
      },
      WORKING_COPY_ORIGIN_SERVICE_PANEL
    );
    yield put(setUseScratchForPreview(true));
    yield put(closeServiceConfigDialog());
    yield put(authFlow.fulfilled({ providerName }));
  }

  storageChannel.close();
  errorChannel.close();
}

/**
 * Handles requests to save a service to the server. Once the service is updated,
 * it will refresh the project schema and optionally trigger bootstrap steps.
 */
export function* saveServiceRequested({ dirty, data }: SaveServiceRoutinePayload): SagaIterator {
  const existingServiceId = yield select(getServiceConfigDialogId);
  const { provider, id } = data;
  const serviceProvider = providers[provider];
  const serviceId = existingServiceId || id || serviceProvider.createId(data);
  const config: ServiceConfigUpdate = serviceProvider.getConfig(data);
  const projectId: string = yield select(getCurrentProjectId);
  const workingCopySchema = useWorkingCopyStore.getState().workingCopyCompat;
  const currentUserSchema = yield select(getUserSchema);
  const activeSchema = workingCopySchema ?? currentUserSchema;
  const scratchUserSchema: ProjectSchemaCompat | undefined = yield select(getScratchUserSchema);

  const existingService = activeSchema?.services?.[serviceId];

  if (config.options && existingService?.options) {
    config.options = {
      ...existingService.options,
      ...config.options,
      ...pick(existingService.options, PRESERVED_OPTIONS)
    };
  }

  // Set introspectedAt if absent
  if (serviceProvider.introspectionType && config.options && !config.options.introspectedAt) {
    config.options.introspectedAt = new Date().toISOString();
  }

  let authentication;

  if (shouldUpdateAuthString(config, dirty)) {
    if (!scratchUserSchema) {
      const meta: PostSchemaRoutineMeta = { branch: SCRATCH_BRANCH };
      yield put(postSchemaRoutine.requested(activeSchema, meta));
      const success = yield call(waitForPostSchema, meta);
      if (!success) {
        return;
      }
    }

    const { data: encryptedAuth } = yield call(encryptServiceAuth, {
      projectId,
      authentication: config.authentication
    });

    authentication = encryptedAuth;
  } else {
    authentication = existingService?.authentication;
  }

  const serviceConfig = {
    id: serviceId,
    title: '',
    ...config,
    authentication
  };

  const newSchema = {
    ...activeSchema,
    services: {
      ...activeSchema.services,
      [serviceId]: serviceConfig
    }
  };

  const shouldSave = serviceProvider.getHealthCheck
    ? yield call(checkServiceHealth, serviceId, serviceConfig as ServiceConfigJSON)
    : true;

  if (!shouldSave) {
    yield put(submitFormCancelled(SERVICE_FORM_NAME));
    return;
  }

  yield call(useWorkingCopyStore.getState().setWorkingCopy, newSchema, WORKING_COPY_ORIGIN_SERVICE_PANEL);
  yield put(
    postSchemaRoutine.requested(newSchema, {
      branch: SCRATCH_BRANCH,
      runtime: true
    })
  );

  const action = yield take([postSchemaRoutine.fulfilled, postSchemaRoutine.rejected]);

  if (action.type === 'branches/POST_SCHEMA_FULFILLED') {
    yield put(setUseScratchForPreview(true));

    yield put(closeServiceConfigDialog());
  } else {
    yield put(showMessage('error', 'Could not update scratch schema'));
  }
}

/**
 * Bootstrap was requested, either manually or because a service was created. Show the dialog.
 */
export function* handleStartBootstrap({ payload }: ActionType<typeof startBootstrap>): SagaIterator {
  yield call(showBootstrapDialog, payload);
}

/**
 * Show the bootstrap dialog which lets the user customize their bootstrap operation(s)
 */
export function* showBootstrapDialog(serviceId: string): SagaIterator {
  const serviceConfig: AnyServiceConfig | undefined = yield select(getServiceById, { serviceId });

  yield put(toggleBootstrapDialog(true));
  yield put(setBootstrapDialogProvider(serviceConfig?.provider ?? ''));

  const { confirm } = yield race({
    confirm: take(bootstrapConfirm),
    cancel: take(bootstrapCancel)
  });

  yield put(toggleBootstrapDialog(false));
  if (confirm) {
    yield put(runBootstrapRoutine.requested({ params: confirm.payload }, { serviceId }));
  }
}

/**
 * Bootstrap content for this Shape
 */
export function bootstrapShape(
  projectSchema: ProjectSchemaJSON,
  serviceId: string,
  serviceObjectType: string,
  shapeName: string
): CallEffect {
  const shape = projectSchema.shapes[shapeName];
  const serviceObjectFields = getServiceObjectFields(projectSchema, shape, 'shopify');
  let idFieldName;
  if (serviceObjectFields.length === 1) {
    idFieldName = serviceObjectFields[0].generators[SERVICE_OBJECT_PATTERN_NAME].idFieldName;
  } else {
    throw new Error('Not implemented');
  }

  const input = {
    serviceId,
    serviceObjectType,
    idFieldName,
    shapeName
  };
  return call(bootstrapShopify, input);
}

export type CreateShapeForBootstrapArgs = {
  projectSchema: ProjectSchemaCompat;
  service: string;
  provider: string;
  serviceObjectType: string;
  newShapeName: string;
};

/**
 * Create a Shape with a service pattern
 */
export function createShapeForBootstrap(args: CreateShapeForBootstrapArgs) {
  const { projectSchema, service, provider, serviceObjectType, newShapeName } = args;
  return function* (): SagaIterator {
    if (projectSchema.shapes[newShapeName] !== undefined) {
      throw new Error(`Shape ${newShapeName} already exists.`);
    }

    yield put(showMessage('info', `Creating ${newShapeName} Shape...`, 1500));
    const shape: ShapeWithObjectSchema = {
      name: newShapeName,
      id: shortid.generate(),
      title: newShapeName,
      workflow: 'default',
      model: { type: 'multiple' },
      schema: {
        type: 'object',
        properties: {
          name: {
            title: 'Name',
            type: 'string',
            description: `Initialized with title from ${provider}`,
            '@mapping': `shapedb:${newShapeName}.${shortid.generate()}`
          }
        }
      }
    };
    const form = {
      properties: { name: { widget: 'singleLineText' } },
      order: ['name']
    };
    const schemaUpdate = getServicePatternSchemaUpdate(
      projectSchema,
      shape,
      form,
      service,
      provider,
      serviceObjectType
    );

    const addServicePatternResult = yield put(
      updateSchema.requested(schemaUpdate, {
        operation: 'add',
        shapeId: schemaUpdate.shapes[newShapeName]?.id
      })
    );

    if (addServicePatternResult.type === updateSchema.REQUESTED) {
      const updateSchemaResult = yield take([updateSchema.fulfilled, updateSchema.rejected]);
      if (updateSchemaResult.type === updateSchema.REJECTED) {
        throw new Error(`Could not create Shape ${newShapeName}`);
      }
    }
  };
}

export type PrepareExistingShapeForBootstrap = {
  projectSchema: ProjectSchemaCompat;
  shapeName: string;
  serviceId: string;
  serviceProvider: string;
  serviceObjectType: string;
};

/**
 * Add service pattern to a Shape if it doesn't already have one.
 */
export function prepareExistingShapeForBootstrap(args: PrepareExistingShapeForBootstrap) {
  const { projectSchema, shapeName, serviceId, serviceProvider, serviceObjectType } = args;
  return function* (): SagaIterator {
    const shape = projectSchema.shapes[shapeName] as ShapeWithObjectSchema;
    const form = projectSchema.forms?.[shapeName].default ?? {
      properties: {},
      order: []
    };
    const serviceInfo = getServiceInfo(projectSchema, shape);
    if (serviceInfo[serviceProvider] === undefined) {
      yield put(showMessage('info', `Updating ${shapeName} schema`, 1500));
      const schemaUpdate = getServicePatternSchemaUpdate(
        projectSchema,
        shape,
        form,
        serviceId,
        serviceProvider,
        serviceObjectType
      );

      const addServicePatternResult = yield put(
        updateSchema.requested(schemaUpdate, {
          operation: 'update',
          shapeId: shape.id
        })
      );

      if (addServicePatternResult.type === 'branches/UPDATE_SCHEMA_REQUESTED') {
        const updateSchemaResult = yield take([updateSchema.fulfilled, updateSchema.rejected]);
        if (updateSchemaResult.type === 'branches/UPDATE_SCHEMA_REJECTED') {
          throw new Error(`Could not add service property to shape ${shape.name}`);
        }
      }
    }
  };
}

/**
 * Run all requested service data bootstrap operations, updating schema first if needed
 */
export function* runBootstrapRequested(
  { params }: RunBootstrapRoutineRequested,
  { serviceId }: RunBootstrapRoutineMeta
): SagaIterator {
  const serviceConfig: AnyServiceConfig = yield select(getServiceById, {
    serviceId
  });

  let projectSchema = yield select(getSchema);

  const tasks: CallEffect[] = [];

  for (const serviceObjectType in params) {
    if (Object.hasOwn(params, serviceObjectType)) {
      const { selectedForBootstrap, existingShape, newShapeName, selectedShapeName } = params[serviceObjectType];
      if (selectedForBootstrap) {
        const shapeName = existingShape ? selectedShapeName! : newShapeName!;
        if (existingShape) {
          yield call(
            prepareExistingShapeForBootstrap({
              projectSchema,
              shapeName,
              serviceId: serviceConfig.id,
              serviceProvider: serviceConfig.provider,
              serviceObjectType
            })
          );
        } else {
          yield call(
            createShapeForBootstrap({
              projectSchema,
              service: serviceConfig.id,
              provider: serviceConfig.provider,
              serviceObjectType,
              newShapeName: shapeName
            })
          );
        }

        projectSchema = yield select(getSchema);
        tasks.push(bootstrapShape(projectSchema, serviceConfig.id, serviceObjectType, shapeName));
      }
    }
  }

  yield put(showMessage('info', 'Started data import from Shopify', 1500));

  return yield all(tasks);
}

/**
 * Show combined success message for all bootstrap operations
 */
export function* runBootstrapFulfilled(action: ActionType<typeof runBootstrapRoutine.fulfilled>): SagaIterator {
  const finalTotal = action.payload.reduce((totalSoFar, item) => totalSoFar + item.result.totalCreated, 0);
  yield put(showMessage('info', `Successfully imported ${finalTotal} items from Shopify`, 1500));
  yield put(fetchSchemaRoutine.requested(undefined, { runtime: false }));
}

export function* clearSratchStateSaga(): SagaIterator {
  // Clear health checks in client state, to match the state of working copy
  yield put(clearServiceHealthChecks());
}

export function* postSchemaFulfilledSaga({
  meta: { branch = DEFAULT_BRANCH }
}: ActionType<typeof postSchemaRoutine.fulfilled>): SagaIterator {
  if (branch !== SCRATCH_BRANCH) {
    // On deployment, if any health checks are in client state persist those
    // checks to the server for consistency
    const healthChecks: Record<string, ServiceHealth> = yield select(getServiceHealthChecks);
    const serviceIds = Object.keys(healthChecks);

    if (serviceIds.length) {
      const branchArgs: BranchQueryArgs = yield select(getBranchQueryArgs, {});

      // Trigger logged checks on all services currently checked
      for (const serviceId of serviceIds) {
        yield call(checkServiceHealthMutation, {
          input: { ...branchArgs, id: serviceId, writeLog: true }
        });
      }

      // Clear the checks, the client and server state should now match
      yield put(setShouldClearServiceHealthChecks());
    }
  }
}

export default function* root(): SagaIterator {
  yield all([
    takeLatest(authFlow.REQUESTED, oauthFlow),
    takeLatest(authFlow.rejected, showErrorMessageSaga),
    takeLatest(startBootstrap, handleStartBootstrap),
    takeLatest(runBootstrapRoutine.REQUESTED, getRoutineSaga(runBootstrapRoutine, runBootstrapRequested)),
    takeLatest(runBootstrapRoutine.FULFILLED, runBootstrapFulfilled),
    takeLatest(
      runBootstrapRoutine.rejected,
      showMessageSaga('error', getSavingErrorMessage('Importing', 'Shopify products'))
    ),
    takeLatest(saveServiceRoutine.rejected, showMessageSaga('error', getSavingErrorMessage('Saving', 'service'))),
    takeLatest(saveServiceRoutine.REQUESTED, getRoutineSaga(saveServiceRoutine, saveServiceRequested)),
    takeLatest(clearScratchState, clearSratchStateSaga),
    takeLatest(postSchemaRoutine.fulfilled, postSchemaFulfilledSaga)
  ]);
}
